import React, { useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { tokens } from "src/design-system/tokens";

// compact (dashboard card): at most ~6 straight date labels so they never overlap; the Weight Tracker page is unchanged.
const WeightWaistChart = ({ height, chartList, userPre, compact = false }) => {
  const [selectedMetric, setSelectedMetric] = useState("weight");
  const [chartData, setChartData] = useState({ labels: [], seriesData: {} });

  useEffect(() => {
    if (chartList && chartList.length > 0) {
      const metrics = {
        weight: "current_weight",
        waist: "waist",
        chest: "chest",
        hip: "hip",
        arm: "arm",
        thigh: "thigh",
        bodyFat: "body_fat",
      };

      const seriesData = {};
      const seriesLabels = {};

      Object.entries(metrics).forEach(([key, field]) => {
        const filtered = chartList.filter(
          (entry) => entry[field] && entry[field] !== 0
        );

        seriesData[key] = filtered.map((entry) => entry[field]);
        seriesLabels[key] = filtered.map((entry) =>
          new Date(entry.date).toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "short",
          })
        );
      });

      setChartData({ labels: seriesLabels, seriesData });
    }
  }, [chartList]);

  const colors = {
    weight: "#026670",
    waist: "#F4D160",
    chest: "#FF5733",
    hip: "#3498DB",
    arm: "#9B59B6",
    thigh: "#2ECC71",
    bodyFat: "#E74C3C",
  };

  const options = {
    // Dashboard card on phones: fewer date labels so they do not overlap.
    ...(compact && {
      responsive: [{ breakpoint: 600, options: { xaxis: { tickAmount: 3 } } }],
    }),
    chart: {
      type: "area",
      toolbar: { show: false },
      zoom: { enabled: false },
      pan: { enabled: false },
      events: {
        legendClick: (chartContext, seriesIndex, config) => {
          const clickedMetric = Object.keys(chartData.seriesData)[seriesIndex];
          setSelectedMetric(clickedMetric);
        },
      },
    },
    colors: Object.keys(chartData.seriesData).map((metric) =>
      selectedMetric === metric ? colors[metric] : tokens.color.lineStrong
    ),
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.4,
        opacityFrom: 0.5,
        opacityTo: 0,
        stops: [0, 90, 100],
      },
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: (chartData.seriesData[selectedMetric] || []).length === 1 ? 0 : 2,
    },
    // A single check-in in the period shows as a dot (an area of one point draws only a line).
    markers: {
      size: (chartData.seriesData[selectedMetric] || []).length === 1 ? 6 : 0,
      hover: { size: 6 },
    },
    xaxis: {
      categories: chartData.labels[selectedMetric] || [],
      ...(compact && { tickAmount: 6 }),
      labels: {
        ...(compact && {
          rotate: 0,
          hideOverlappingLabels: true,
          style: {
            fontFamily: tokens.font.family,
            fontSize: `${tokens.font.size.xs}px`,
            colors: tokens.color.muted,
          },
        }),
        show: (chartData.labels[selectedMetric] || []).length > 0,
      },
    },
    yaxis: {
      title: {
        text: {
          weight: `Weight (${userPre?.weight_unit})`,
          waist: `Waist (${userPre?.waist_unit})`,
          chest: `Chest (${userPre?.chest_unit})`,
          hip: `Hip (${userPre?.hip_unit})`,
          arm: `Arm (${userPre?.arm_unit})`,
          thigh: `Thigh (${userPre?.thigh_unit})`,
          bodyFat: `bodyFat (%)`,
        }[selectedMetric],
        style: compact
          ? {
              fontFamily: tokens.font.family,
              fontSize: `${tokens.font.size.xs}px`,
              fontWeight: tokens.font.weight.medium,
              color: tokens.color.muted,
            }
          : { fontSize: `${tokens.font.size.md}px` },
      },
      ...(compact && {
        labels: {
          style: {
            fontFamily: tokens.font.family,
            fontSize: `${tokens.font.size.xs}px`,
            colors: tokens.color.muted,
          },
        },
      }),
    },
    legend: {
      position: "top",
      ...(compact && {
        horizontalAlign: "left",
        fontFamily: tokens.font.family,
        fontSize: `${tokens.font.size.xs}px`,
        fontWeight: tokens.font.weight.medium,
        itemMargin: { horizontal: 6, vertical: 4 },
      }),
      showForSingleSeries: true,
      showForNullSeries: true,
      showForZeroSeries: true,
      onItemClick: { toggleDataSeries: false },
      onItemHover: { highlightDataSeries: true },
      formatter: function (seriesName) {
        const labelsMap = {
          weight: `Weight (${userPre?.weight_unit})`,
          waist: `Waist (${userPre?.waist_unit})`,
          chest: `Chest (${userPre?.chest_unit})`,
          hip: `Hip (${userPre?.hip_unit})`,
          arm: `Arm (${userPre?.arm_unit})`,
          thigh: `Thigh (${userPre?.thigh_unit})`,
          bodyFat: compact ? "Body fat (%)" : `bodyFat (%)`,
        };
        return labelsMap[seriesName] || seriesName;
      },
      labels: {
        colors: Object.keys(chartData.seriesData).map((metric) =>
          selectedMetric === metric ? colors[metric] : tokens.color.muted
        ),
      },
      // One markers object (a second one used to replace the small compact dots). ApexCharts 3.5x
      // sizes legend dots by `size` (radius): 4 → an 8px dot beside 12px text on the dashboard.
      markers: {
        fillColors: Object.keys(chartData.seriesData).map((metric) =>
          selectedMetric === metric ? colors[metric] : tokens.color.muted
        ),
        ...(compact && { size: tokens.space[1], offsetX: -2 }),
      },
    },
  };

  const seriesData = {};
  Object.keys(chartData.seriesData).forEach((metric) => {
    seriesData[metric] = chartData.seriesData[metric].map(
      (value) => value ?? 0
    );
  });

  const series = Object.keys(chartData.seriesData).map((metric) => ({
    name: metric,
    data: selectedMetric === metric ? chartData.seriesData[metric] || [] : [],
  }));

  return (
    <Chart
      options={options}
      series={series}
      type="area"
      height={height ? height : 550}
    />
  );
};

export default WeightWaistChart;
